import SslSupportService from '../ssl-support-service/ssl-support-service';

const INVALID = 'invalid';
const IMPOSSIBLE_TO_CHECK_ERROR = 'impossibleToCheckError';
const IMPOSSIBLE_TO_CHECK_WARNING = 'impossibleToCheckWarning';
const IMPOSSIBLE_TO_CHECK_NEW_SERVER_CERT_WARNING = 'impossibleToCheckNewServerCertWarning';
const READY_WARN = 'readyToCheckAndNeedUserAcceptanceOnSuccess';
const CHECK_TIMEOUT = 10/*s*/ * 1000;

const STATUS = {
  VALIDATING: 'validating',
  CHECKING_AVAILABILITY: 'checking_availability'
};

export default class BaseUrlValidator {
  /*@ngInject*/
  constructor($q, $http, installerResource, fields, i18n, wizardInfo) {
    this.$q = $q;
    this.$http = $http;
    this.fields = fields;
    this.resource = installerResource;
    this.i18n = i18n;
    this.wizardInfo = wizardInfo;
    this.sslSupportService = new SslSupportService();
    this.status = null;
  }

  validateNonSecureUrl(nonSecureUrl, listenAddress, listenPort) {
    const model = {};
    model[this.fields.BASE_URL] = nonSecureUrl;
    model[this.fields.SERVER_CONNECTOR_MODE_SETTING] = {};
    model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.LISTEN_ADDRESS] = listenAddress;
    model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.NON_SECURE_LISTEN_PORT] = listenPort;
    model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_MODE] = 'disable';
    return this.validate(model);
  }

  validate(model) {
    this.status = STATUS.VALIDATING;
    const url = model[this.fields.BASE_URL];

    // eslint-disable-next-line new-cap
    const connection = {};
    connection[this.fields.BASE_URL] = url;
    connection[this.fields.LISTEN_ADDRESS] = model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.LISTEN_ADDRESS];
    connection[this.fields.NON_SECURE_LISTEN_PORT] = model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.NON_SECURE_LISTEN_PORT];
    connection[this.fields.REDIRECTION_FROM_HTTP_FLAG] = model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.REDIRECTION_FROM_HTTP_FLAG];
    connection[this.fields.SECURE_MODE] = model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_MODE];
    connection[this.fields.SECURE_LISTEN_PORT] = model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_LISTEN_PORT];
    connection[this.fields.SECURE_CONTENT] = model[this.fields.SECURE_CONTENT];
    connection[this.fields.USE_OLD_SERVER_CERT] = model[this.fields.USE_OLD_SERVER_CERT];

    let endpoint;

    return this.resource.connections.save(connection).$promise.
      then(res => {
        endpoint = res;
        if (endpoint.testEndpointState === INVALID || endpoint.testEndpointState === IMPOSSIBLE_TO_CHECK_ERROR) {
          return this.$q.reject({error: endpoint.stateComment});
        }
        if (endpoint.testEndpointState === IMPOSSIBLE_TO_CHECK_WARNING || endpoint.testEndpointState === IMPOSSIBLE_TO_CHECK_NEW_SERVER_CERT_WARNING) {
          return this.$q.reject(this.getWarning(endpoint.stateComment, endpoint));
        }
        return res;
      }).
      catch(err => (
        this.$q.reject(this.getTruncatedError(err))
      )).
      then(() => {
        this.status = STATUS.CHECKING_AVAILABILITY;
        return this.$http.get(endpoint[this.fields.STATE_TEST_URL], {
          timeout: CHECK_TIMEOUT,
          __wizardDoNotInterceptOnError: true
        }).
          catch(error => {
            if (endpoint[this.fields.BASE_URL_BACKEND_ACCESS_STATE] === 'untrustedCertificate') {
              return this.$q.reject(this.getWarning(this.getUntrustedCertificateWarning(url), endpoint));
            } else {
              const truncatedError = this.getTruncatedError(error);
              const truncatedErrorMessage = truncatedError.error ? `: ${ truncatedError.error}` : '';
              const listenPort = this.isSecureMode(model) ? connection[this.fields.SECURE_LISTEN_PORT] : connection[this.fields.NON_SECURE_LISTEN_PORT];
              const dockerRelatedPart = (this.wizardInfo.installationType !== 'DOCKER') ? '' : ` Verify that the listen port ${listenPort} was properly exposed or published on startup of the Docker container`;
              return this.$q.reject(
                {error: `The service isn't accessible from your browser at this URL.${dockerRelatedPart}${truncatedErrorMessage}`}
              );
            }
          });
      }).
      then(() => {
        if (endpoint[this.fields.BASE_URL_BACKEND_ACCESS_STATE] === 'fail' && this.isSecureMode(model)) {
          return this.$q.reject(
            {error: `The service cannot be accessed from the installation server using the specified base URL. \n 
            This type of access is required for communication over TLS between the service and the internal services 
            that are bundled with the ${this.wizardInfo.productName} installation. \n
            Check the internal DNS records on your server and make sure that its hostname is resolved correctly.`}
          );
        }
        return (endpoint.testEndpointState === READY_WARN)
          ? this.$q.reject(this.getWarning(endpoint[this.fields.STATE_COMMENT], endpoint))
          : {};
      }).
      finally(() => {
        this.status = null;
        //Don't return stop promise to avoid showing unneeded errors if stop fails
        if (endpoint && endpoint.id) {
          this.resource.connections.delete({id: endpoint.id});
        }
      });
  }

  getUntrustedCertificateWarning(baseUrl) {
    return this.i18n('The service is not available from your computer by the given Base URL {{baseUrl}}.\n' +
      'Most likely server certificate is untrusted, in that case end users will see corresponding warnings in browser while opening the service.\n' +
      'If you still want to proceed click Continue.\n' +
      'Otherwise, click Cancel and upload server certificate signed by trusted certification authority.', {baseUrl});
  }

  isSecureMode(model) {
    return model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_MODE] === this.sslSupportService.TLS_MODE;
  }

  getTruncatedError(err) {
    return !err.warning && err.data && !err.data.errors && angular.isString(err.data)
      ? {error: `${err.data.substr(0, 200) }...`}
      : err;
  }

  getWarning(warningMessage, endpoint) {
    return {
      warning: warningMessage,
      baseUrlBackendAccessState: endpoint[this.fields.BASE_URL_BACKEND_ACCESS_STATE] || null,
      testEndpointState: endpoint[this.fields.TEST_ENDPOINT_STATE] || null
    };
  }
}



// WEBPACK FOOTER //
// ./src/blocks/base-url/base-url__validator.js